import { Users, Clock, TrendingUp, AlertCircle, Settings, X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Phone, Eye, UserMinus, PhoneOff, Mail, Info, Loader2, Search, Check } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/app/_module/components/ui/tooltip";
import React, { useState, useMemo, useEffect } from "react";
import { useRouter } from "next/navigation";

import { Badge } from "@/app/_module/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Progress } from "@/app/_module/components/ui/progress";
import { Button } from "@/app/_module/components/ui/button";
import { Input } from "@/app/_module/components/ui/input";
import { Dialog, ResponsiveDialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/app/_module/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/app/_module/components/ui/select";
import { useAgentLeads, AGENT_LEADS_PAGE_SIZE } from "../hooks/queries";
import { useUnassignLead } from "../hooks/useUnassignLead";
import { useToast } from "@/app/_module/hooks/use-toast";
import { Checkbox } from "@/app/_module/components/ui/checkbox";

interface AssignedBreakdownRow {
  sourceAgencyId: string | null;
  sourceAgencyName: string;
  assignedByUserId: string | null;
  assignedByName: string;
  count: number;
}

interface Agent {
  agentId: string;
  agentName: string;
  agencyName?: string; // Agency name for sub-agency identification
  email: string;
  activeLeadsCount: number;
  ownedLeads?: number; // Leads this agent uploaded/owns themselves (user_id), household-deduped
  // Distinct leads this agent actually WORKED ON — call, note, SMS,
  // appointment or door knock. Independent of assignment: setters working a
  // shared book show ~0 assigned no matter how much they do.
  workedLeads?: number;
  // The owner/admin shared book for this agent's agency. Same figure for every
  // owner/admin there, so it must never be summed across the roster.
  sharedBookLeads?: number;
  completedLeads?: number; // Number of "Sold & Approved" leads (for progress bar)
  maxLeads: number;
  leadGoal?: number; // Custom goal/target set by agency (e.g., 100)
  goalType?: string | null;     // lead_count | dials | talk_time | completed_calls | contacts | appointments
  goalValue?: number | null;    // target for goalType
  goalDuration?: string | null; // daily | weekly | monthly
  goalCurrent?: number | null;  // current value of the chosen metric in its window
  // Mirrors UserStatusSelector statuses (available, idle, on_call, dnd, offline).
  // Legacy values (online/active/busy) accepted for backward compat during deploy
  // rollout — normalized below in getStatusColor / formatStatus.
  status: "available" | "idle" | "on_call" | "dnd" | "offline" | "online" | "active" | "busy";
  lastActivity: string | null;
  conversionRate: number;
  avgResponseTime: string;
  assignedBreakdown?: AssignedBreakdownRow[];
}

export interface GoalConfig {
  goalType: string;
  goalValue: number;
  goalDuration: string;
}

// Compact big lead counts so the badges stay readable: 1500 -> "1.5k", 100000 -> "100k", 1200000 -> "1.2m".
// Under 1,000 stays exact. The full number is still available on hover/title.
const formatCompact = (n: number | null | undefined): string => {
  const v = Number(n ?? 0);
  if (!isFinite(v)) return "0";
  if (Math.abs(v) < 1000) return String(v);
  return new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 })
    .format(v)
    .toLowerCase();
};

// Display metadata for each goal metric.
//
// `prefix` renders before the number (only premium uses it). Everything else keeps
// using `unit` as a suffix, so existing metrics are untouched.
export const GOAL_METRICS: Record<
  string,
  { label: string; short: string; unit: string; prefix?: string }
> = {
  lead_count: { label: "Lead Assigned Count", short: "Leads", unit: "" },
  dials: { label: "Dials Per Day", short: "Dials", unit: "" },
  talk_time: { label: "Total Talk Time", short: "Talk", unit: "m" },
  completed_calls: { label: "Completed Calls", short: "Completed", unit: "" },
  contacts: { label: "Contacts Made", short: "Contacts", unit: "" },
  appointments: { label: "Appointments Set", short: "Appts", unit: "" },
  // Sales quotas. Premium is whole dollars of annual premium, dated the same way the
  // dashboard's premium boxes date a sale (uwr_date, else the day the sale was logged),
  // so an agent's quota bar and the premium figure agree.
  premium: { label: "Premium Sold ($)", short: "Premium", unit: "", prefix: "$" },
  policies_submitted: { label: "Policies Submitted", short: "Policies", unit: "" },
};

interface AgentWorkloadProps {
  agents: Agent[];
  isLoading?: boolean;
  // `silent` suppresses the per-agent success toast. "Apply to All" calls this once
  // per agent, which fired N identical toasts stacked down the screen; the bulk path
  // sets it and emits ONE summary instead. Errors always toast, silent or not.
  onUpdateLeadGoal?: (
    agentId: string,
    goal: GoalConfig | null,
    opts?: { silent?: boolean },
  ) => Promise<void>;
}

// Disposition colors matching UnassignedLeads — inline styles for guaranteed rendering
const getLeadStatusInline = (status: string | undefined): React.CSSProperties => {
  const normalized = (status || '').toLowerCase();
  switch (normalized) {
    case 'sold & approved':
    case 'sold':
    case 'approved':
      return { backgroundColor: '#059669', color: '#d1fae5' }; // emerald-600
    case 'appt':
    case 'appointment':
    case 'scheduled':
      return { backgroundColor: '#0891b2', color: '#cffafe' }; // cyan-600
    case 'underwriting':
    case 'under writing':
      return { backgroundColor: '#f59e0b', color: '#fef3c7' }; // amber-500
    case 'new lead':
    case 'new':
      return { backgroundColor: '#dbeafe', color: '#1e40af' }; // blue-100 / blue-800
    case 'bad number':
    case 'bad #':
      return { backgroundColor: '#ef4444', color: '#fee2e2' }; // red-500
    case 'no sale':
      return { backgroundColor: '#dc2626', color: '#fee2e2' }; // red-600
    case 'do not call':
      return { backgroundColor: '#92400e', color: '#fef3c7' }; // amber-800
    case 'disposed':
      return { backgroundColor: '#4b5563', color: '#f3f4f6' }; // gray-600
    default:
      return { backgroundColor: '#4b5563', color: '#f3f4f6' }; // gray-600
  }
};

// Normalize status display name (New Lead -> Leads)
const getStatusDisplayName = (status: string): string => {
  const normalized = status?.toLowerCase();
  if (normalized === 'new lead' || normalized === 'new') {
    return 'Leads';
  }
  return status || 'Leads';
};

export const AgentWorkload = function ({ agents, isLoading, onUpdateLeadGoal }: AgentWorkloadProps) {
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [globalGoal, setGlobalGoal] = useState<string>("");
  const [individualGoals, setIndividualGoals] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  // Configurable goal: which activity metric, and over what window.
  const [goalType, setGoalType] = useState<string>("dials");
  const [goalDuration, setGoalDuration] = useState<string>("daily");
  const [expandedAgentId, setExpandedAgentId] = useState<string | null>(null);
  const [agentLeadsPage, setAgentLeadsPage] = useState(1);
  const [agentLeadsTotal, setAgentLeadsTotal] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState<string>("leads-desc");
  const [searchQuery, setSearchQuery] = useState("");
  // Goals dialog: its own search + selection, kept separate from the page-level
  // `searchQuery` so filtering the dialog doesn't reshuffle the list behind it.
  const [goalSearch, setGoalSearch] = useState("");
  const [selectedAgentIds, setSelectedAgentIds] = useState<Set<string>>(new Set());
  // Sub-agency narrowing. Empty = no filter (show all), so unticking the last agency
  // can't strand the dialog on an empty list with no obvious way back.
  const [goalAgencyFilter, setGoalAgencyFilter] = useState<Set<string>>(new Set());
  const router = useRouter();
  const { toast } = useToast();
  const unassignMutation = useUnassignLead();

  const sortedAgents = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = q
      ? agents.filter(
          (a) =>
            (a.agentName || "").toLowerCase().includes(q) ||
            (a.email || "").toLowerCase().includes(q) ||
            (a.agencyName || "").toLowerCase().includes(q),
        )
      : agents;
    const sorted = [...filtered];
    switch (sortBy) {
      case "name-asc":
        sorted.sort((a, b) => a.agentName.localeCompare(b.agentName));
        break;
      case "leads-desc":
        sorted.sort((a, b) => b.activeLeadsCount - a.activeLeadsCount);
        break;
      case "leads-asc":
        sorted.sort((a, b) => a.activeLeadsCount - b.activeLeadsCount);
        break;
      case "conversion-desc":
        sorted.sort((a, b) => b.conversionRate - a.conversionRate);
        break;
      case "goal-progress":
        sorted.sort((a, b) => {
          const aHasGoal = a.leadGoal && a.leadGoal > 0;
          const bHasGoal = b.leadGoal && b.leadGoal > 0;
          if (!aHasGoal && !bHasGoal) return 0;
          if (!aHasGoal) return 1;
          if (!bHasGoal) return -1;
          const aRatio = (a.completedLeads || 0) / a.leadGoal!;
          const bRatio = (b.completedLeads || 0) / b.leadGoal!;
          return bRatio - aRatio;
        });
        break;
      case "calltime-desc": {
        const parseTime = (t: string): number => {
          let mins = 0;
          const hMatch = t.match(/(\d+)h/);
          const mMatch = t.match(/(\d+)m/);
          const sMatch = t.match(/(\d+)s/);
          if (hMatch) mins += parseInt(hMatch[1]) * 60;
          if (mMatch) mins += parseInt(mMatch[1]);
          if (sMatch) mins += parseInt(sMatch[1]) / 60;
          return mins;
        };
        sorted.sort((a, b) => parseTime(b.avgResponseTime) - parseTime(a.avgResponseTime));
        break;
      }
      case "activity-desc":
        sorted.sort((a, b) => {
          if (!a.lastActivity && !b.lastActivity) return 0;
          if (!a.lastActivity) return 1;
          if (!b.lastActivity) return -1;
          return new Date(b.lastActivity).getTime() - new Date(a.lastActivity).getTime();
        });
        break;
    }
    return sorted;
  }, [agents, sortBy, searchQuery]);

  // Fetch leads for the expanded agent ONLY — server-side filter via filterAgentId.
  // Previously this used useAllLeads (500-row prefetch + client-side filter), which
  // silently dropped assignments older than the 500 most recent leads in an agency.
  // With server-side filter the window is per-agent and we never miss anything.
  const {
    data: agentLeadsData,
    isLoading: agentLeadsLoading,
    isFetching: agentLeadsFetching,
  } = useAgentLeads(expandedAgentId ?? undefined, agentLeadsPage, {
    enabled: !!expandedAgentId,
  });
  const expandedAgentLeads = agentLeadsData?.leads ?? [];
  const hasNextLeadsPage = expandedAgentLeads.length === AGENT_LEADS_PAGE_SIZE;
  // Backend only returns the count on page 1 — preserve it for the pager label.
  useEffect(() => {
    if (typeof agentLeadsData?.totalItems === "number") setAgentLeadsTotal(agentLeadsData.totalItems);
  }, [agentLeadsData?.totalItems]);
  const agentLeadsTotalPages = agentLeadsTotal != null ? Math.max(1, Math.ceil(agentLeadsTotal / AGENT_LEADS_PAGE_SIZE)) : null;
  // Only show the full-block spinner on the FIRST page load; page-to-page uses
  // keepPreviousData so the list doesn't flash empty.
  const isLoadingExpandedAgent = !!expandedAgentId && agentLeadsLoading && agentLeadsPage === 1;

  // Map keyed by the currently expanded agent id so the render code below
  // (which looks up by agent.agentId) doesn't need to change.
  const agentLeadsMap = useMemo(() => {
    if (!expandedAgentId) return {} as Record<string, typeof expandedAgentLeads>;
    return { [expandedAgentId]: expandedAgentLeads };
  }, [expandedAgentId, expandedAgentLeads]);

  const UNLIMITED_THRESHOLD = 2000000000; // 2B (Enterprise accounts with unlimited leads)
  // Status color + label mapping. Must match UserStatusSelector so the agent's
  // workload row reads the same as their personal status badge.
  // available → green, idle → yellow, on_call → blue, dnd → red, offline → gray.
  // Legacy values (online/active/busy) are normalized to the new ones during the
  // backend deploy rollout window.
  const getStatusColor = (status: string) => {
    switch (status) {
      case "available":
      case "online":  // legacy
      case "active":  // legacy
        return "bg-green-100 text-green-800 border-green-200";
      case "idle":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "on_call":
      case "busy":    // legacy (backend used to remap on_call → busy)
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "dnd":
        return "bg-red-100 text-red-800 border-red-200";
      case "offline":
        return "bg-gray-100 text-gray-600 border-gray-200";
      default:
        return "bg-gray-100 text-gray-600 border-gray-200";
    }
  };

  // Format status for display — mirrors UserStatusSelector labels exactly.
  const formatStatus = (status: string) => {
    switch (status) {
      case "available":
      case "online":  // legacy
      case "active":  // legacy
        return "Available";
      case "idle":
        return "Idle";
      case "on_call":
      case "busy":    // legacy (backend used to remap on_call → busy)
        return "On Call";
      case "dnd":
        return "DND";
      case "offline":
        return "Offline";
      default:
        return status;
    }
  };

  const getWorkloadColor = (current: number, goal?: number) => {
    if (!goal || goal <= 0) return "text-gray-600"; // No goal set
    const percentage = (current / goal) * 100;
    if (percentage >= 90) return "text-red-600";
    if (percentage >= 75) return "text-yellow-600";
    return "text-green-600";
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No activity yet';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'No activity yet';
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (e) {
      return 'No activity yet';
    }
  };

  const formatPhone = (phone: string) => {
    if (!phone) return 'N/A';
    const digits = phone.replace(/\D/g, '');
    // Strip leading country code ("1" for US/CA) — the row is tight and "+1" is noise here.
    const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
    if (local.length === 10) return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
    return phone;
  };

  const getDaysAgo = (dateString: string | null): number | null => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    return Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  };

  const handleUnassign = (leadId: string, agentId: string) => {
    unassignMutation.mutate({
      leadIds: [leadId],
      context: { agentId },
    });
  };

  // Agents shown in the goals dialog, filtered by its own search box. Matches agent
  // name AND agency name — scanning a long roster, a manager wants "everyone in Gotham
  // North" about as often as one person.
  const goalDialogAgents = useMemo(() => {
    const q = goalSearch.trim().toLowerCase();
    return agents.filter((a) => {
      if (goalAgencyFilter.size > 0 && !goalAgencyFilter.has(a.agencyName || "")) return false;
      if (!q) return true;
      return `${a.agentName || ""} ${a.agencyName || ""}`.toLowerCase().includes(q);
    });
  }, [agents, goalSearch, goalAgencyFilter]);

  // Sub-agencies present in the roster, for the filter chips. Derived from the agents
  // themselves so it can only ever offer agencies that actually have someone to set a
  // goal for.
  const goalAgencies = useMemo(() => {
    const names = new Set<string>();
    for (const a of agents) if (a.agencyName) names.add(a.agencyName);
    return Array.from(names).sort();
  }, [agents]);

  const toggleGoalAgency = (name: string) =>
    setGoalAgencyFilter((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  // Only ever counts agents currently VISIBLE. Selecting people, then filtering them
  // out of view, then hitting Apply would otherwise silently write to agents the user
  // can no longer see.
  const visibleSelectedIds = useMemo(
    () => goalDialogAgents.filter((a) => selectedAgentIds.has(a.agentId)).map((a) => a.agentId),
    [goalDialogAgents, selectedAgentIds],
  );
  const allVisibleSelected =
    goalDialogAgents.length > 0 && visibleSelectedIds.length === goalDialogAgents.length;

  const toggleAgentSelected = (agentId: string) => {
    setSelectedAgentIds((prev) => {
      const next = new Set(prev);
      if (next.has(agentId)) next.delete(agentId);
      else next.add(agentId);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setSelectedAgentIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) goalDialogAgents.forEach((a) => next.delete(a.agentId));
      else goalDialogAgents.forEach((a) => next.add(a.agentId));
      return next;
    });
  };

  const handleApplyGlobalGoal = async () => {
    if (!onUpdateLeadGoal) return;

    const goalValue = parseInt(globalGoal);
    if (isNaN(goalValue) || goalValue <= 0) {
      alert("Please enter a valid goal number");
      return;
    }

    // Selected agents if any are ticked, else everyone currently visible. Falling back
    // to the FILTERED list rather than `agents` matters: after a search the button says
    // "Apply to All (3)" and must mean those 3, not the whole roster.
    const targets = visibleSelectedIds.length
      ? goalDialogAgents.filter((a) => selectedAgentIds.has(a.agentId))
      : goalDialogAgents;
    if (targets.length === 0) return;

    setIsSaving(true);
    try {
      await Promise.all(
        targets.map(agent =>
          onUpdateLeadGoal(agent.agentId, { goalType, goalValue, goalDuration }, { silent: true }),
        )
      );
      const m = GOAL_METRICS[goalType];
      toast({
        title: "Goal applied",
        description: `${m?.prefix || ""}${goalValue.toLocaleString("en-US")}${m?.unit || ""} ${
          m?.short || goalType
        } (${goalDuration}) set for ${targets.length} ${targets.length === 1 ? "agent" : "agents"}.`,
      });
      setGlobalGoal("");
      setSelectedAgentIds(new Set());
      setIsSettingsOpen(false);
    } catch (error) {
      console.error("Error applying global goal:", error);
      alert(error instanceof Error && error.message ? error.message : "Failed to apply global goal");
    } finally {
      setIsSaving(false);
    }
  };

  const handleApplyIndividualGoal = async (agentId: string) => {
    if (!onUpdateLeadGoal) return;

    const goalStr = individualGoals[agentId];
    const goalValue = goalStr ? parseInt(goalStr) : null;

    if (goalStr && (isNaN(goalValue!) || goalValue! <= 0)) {
      alert("Please enter a valid goal number");
      return;
    }

    setIsSaving(true);
    try {
      await onUpdateLeadGoal(agentId, goalValue != null ? { goalType, goalValue, goalDuration } : null);
      setIndividualGoals(prev => {
        const updated = { ...prev };
        delete updated[agentId];
        return updated;
      });
    } catch (error) {
      console.error("Error applying individual goal:", error);
      alert(error instanceof Error && error.message ? error.message : "Failed to apply goal");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearGoal = async (agentId: string) => {
    if (!onUpdateLeadGoal) return;

    setIsSaving(true);
    try {
      await onUpdateLeadGoal(agentId, null);
    } catch (error) {
      console.error("Error clearing goal:", error);
      alert("Failed to clear goal");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="flex flex-col min-h-[420px] h-[calc(100vh-160px)]">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold">Agent Workload</CardTitle>
            <div className="animate-pulse rounded bg-gray-200 h-8 w-24" />
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="animate-pulse rounded bg-gray-200 h-5 w-36" />
                      <div className="animate-pulse rounded-full bg-gray-200 h-5 w-14" />
                    </div>
                    <div className="animate-pulse rounded bg-gray-200 h-4 w-44" />
                  </div>
                  <div className="text-right space-y-1">
                    <div className="animate-pulse rounded bg-gray-200 h-6 w-12 ml-auto" />
                    <div className="animate-pulse rounded bg-gray-200 h-3 w-20 ml-auto" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="animate-pulse rounded bg-gray-200 h-3 w-16" />
                    <div className="animate-pulse rounded bg-gray-200 h-3 w-10" />
                  </div>
                  <div className="animate-pulse rounded-full bg-gray-200 h-2 w-full" />
                </div>
                <div className="grid grid-cols-2 gap-4 mt-3">
                  <div className="animate-pulse rounded bg-gray-200 h-4 w-28" />
                  <div className="animate-pulse rounded bg-gray-200 h-4 w-32" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex flex-col min-h-[420px] h-[calc(100vh-160px)]">
      <CardHeader className="flex-shrink-0 space-y-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">Agent Workload</CardTitle>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search agents..."
                className="h-8 w-[160px] pl-8 text-xs"
              />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="h-8 w-[150px] text-xs">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name-asc">Name (A-Z)</SelectItem>
                <SelectItem value="leads-desc">Most Leads</SelectItem>
                <SelectItem value="leads-asc">Least Leads</SelectItem>
                <SelectItem value="conversion-desc">Highest Conversion</SelectItem>
                <SelectItem value="goal-progress">Goal Progress</SelectItem>
                <SelectItem value="calltime-desc">Most Call Time</SelectItem>
                <SelectItem value="activity-desc">Recent Activity</SelectItem>
              </SelectContent>
            </Select>
          {onUpdateLeadGoal && (
            <Dialog
              open={isSettingsOpen}
              onOpenChange={(open) => {
                setIsSettingsOpen(open);
                // Reset search + selection on close. Persisting them means reopening
                // later shows a stale set of ticked agents, and "Apply" would silently
                // target whoever was picked in a previous session.
                if (!open) {
                  setGoalSearch("");
                  setSelectedAgentIds(new Set());
                  setGoalAgencyFilter(new Set());
                }
              }}
            >
              <DialogTrigger asChild>
                <Button variant="outline" size="sm">
                  <Settings className="h-4 w-4 mr-2" />
                  Set Goals
                </Button>
              </DialogTrigger>
              <ResponsiveDialogContent size="2xl" className="m-goals-dialog max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Set &amp; Configure Agent Goals</DialogTitle>
                </DialogHeader>
                <div className="space-y-6 py-4">
                  {/* Goal Type Configurator */}
                  <div className="border-b pb-4">
                    <h3 className="text-sm font-medium mb-1">Goal Type</h3>
                    <p className="text-xs text-gray-500 mb-3">
                      Point agents at the activity that drives sales — not just raw lead counts.
                    </p>
                    <div className="flex gap-2">
                      <Select value={goalType} onValueChange={setGoalType}>
                        <SelectTrigger className="flex-1">
                          <SelectValue placeholder="Goal metric" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(GOAL_METRICS).map(([value, m]) => (
                            <SelectItem key={value} value={value}>{m.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={goalDuration} onValueChange={setGoalDuration}>
                        <SelectTrigger className="w-[140px]">
                          <SelectValue placeholder="Duration" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="daily">Daily</SelectItem>
                          <SelectItem value="weekly">Weekly</SelectItem>
                          <SelectItem value="monthly">Monthly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Apply target value to all */}
                  <div className="border-b pb-4">
                    <h3 className="text-sm font-medium mb-3">
                      Apply to All — {GOAL_METRICS[goalType]?.label} target ({goalDuration})
                    </h3>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        // No hardcoded "e.g. 150" — it was nonsense next to a dollar
                        // quota (a $150/month premium target) and next to policy counts.
                        placeholder={`Target ${(GOAL_METRICS[goalType]?.short || 'value').toLowerCase()}${
                          GOAL_METRICS[goalType]?.prefix ? ' in dollars' : ''
                        }`}
                        value={globalGoal}
                        onChange={(e) => setGlobalGoal(e.target.value)}
                        className="flex-1"
                      />
                      <Button
                        onClick={handleApplyGlobalGoal}
                        disabled={isSaving || !globalGoal || goalDialogAgents.length === 0}
                      >
                        {visibleSelectedIds.length > 0
                          ? `Apply to ${visibleSelectedIds.length} Selected`
                          : `Apply to All${
                              goalDialogAgents.length !== agents.length
                                ? ` (${goalDialogAgents.length})`
                                : ""
                            }`}
                      </Button>
                    </div>
                  </div>

                  {/* Individual Agent Goals */}
                  <div>
                    <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                      <h3 className="text-sm font-medium">
                        Individual Agent Goals
                        <span className="ml-2 text-xs font-normal text-gray-500">
                          {/* "X of Y" whenever ANY filter is narrowing the list — search
                              or agency. Keying this off search alone showed the full
                              roster count above a filtered list. */}
                          {goalDialogAgents.length !== agents.length
                            ? `${goalDialogAgents.length} of ${agents.length}`
                            : `${agents.length}`}
                          {visibleSelectedIds.length > 0 && ` · ${visibleSelectedIds.length} selected`}
                        </span>
                      </h3>
                      {goalDialogAgents.length > 0 && (
                        <button
                          type="button"
                          onClick={toggleSelectAllVisible}
                          className="text-xs font-semibold text-primary hover:underline"
                        >
                          {allVisibleSelected ? "Clear selection" : "Select all"}
                        </button>
                      )}
                    </div>

                    <Input
                      placeholder="Search agents or agency…"
                      value={goalSearch}
                      onChange={(e) => setGoalSearch(e.target.value)}
                      className="mb-3"
                    />

                    {/* Sub-agency filter. Only rendered when there's more than one to
                        choose between — a single-agency owner gains nothing from a
                        control that can only ever mean "all".
                        Narrowing here also scopes the bulk button, because Apply falls
                        back to this filtered list: pick "Gotham North", hit Apply to All,
                        and only Gotham North is written. */}
                    {goalAgencies.length > 1 && (
                      <div className="flex flex-wrap items-center gap-1.5 mb-3 max-h-[68px] overflow-y-auto">
                        <button
                          type="button"
                          onClick={() => setGoalAgencyFilter(new Set())}
                          className={`text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${
                            goalAgencyFilter.size === 0
                              ? "bg-primary text-white border-primary"
                              : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                          }`}
                        >
                          All agencies
                        </button>
                        {goalAgencies.map((name) => {
                          const on = goalAgencyFilter.has(name);
                          return (
                            <button
                              key={name}
                              type="button"
                              onClick={() => toggleGoalAgency(name)}
                              className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors ${
                                on
                                  ? "bg-primary/10 text-primary border-primary/40"
                                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                              }`}
                            >
                              <span
                                className={`h-2.5 w-2.5 rounded-[3px] border flex items-center justify-center ${
                                  on ? "bg-primary border-primary" : "border-gray-300"
                                }`}
                              >
                                {on && <Check className="h-2 w-2 text-white" />}
                              </span>
                              {name}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {goalDialogAgents.length === 0 ? (
                      <p className="text-sm text-gray-500 py-6 text-center">
                        No agents match &ldquo;{goalSearch}&rdquo;.
                      </p>
                    ) : (
                    // Scrolls independently of the dialog. Without this the roster
                    // pushed the goal-type and Apply controls off-screen, so setting a
                    // goal for a large agency meant scrolling back up to reach the button.
                    <div className="space-y-3 max-h-[45vh] overflow-y-auto pr-1">
                      {goalDialogAgents.map((agent) => (
                        <div
                          key={agent.agentId}
                          className={`flex items-center gap-2 p-3 border rounded-lg ${
                            selectedAgentIds.has(agent.agentId) ? "border-primary/40 bg-primary/[0.03]" : ""
                          }`}
                        >
                          <Checkbox
                            checked={selectedAgentIds.has(agent.agentId)}
                            onCheckedChange={() => toggleAgentSelected(agent.agentId)}
                            aria-label={`Select ${agent.agentName}`}
                            className="mr-1"
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              {agent.agentName}
                              {agent.agencyName && (
                                <span className="text-primary font-normal"> - {agent.agencyName}</span>
                              )}
                            </p>
                            <p className="text-xs text-gray-500">
                              {agent.goalValue
                                ? `Current: ${GOAL_METRICS[agent.goalType || 'lead_count']?.prefix || ''}${Number(agent.goalCurrent ?? 0).toLocaleString('en-US')} / ${GOAL_METRICS[agent.goalType || 'lead_count']?.prefix || ''}${Number(agent.goalValue).toLocaleString('en-US')} ${GOAL_METRICS[agent.goalType || 'lead_count']?.short || ''} (${agent.goalDuration || 'daily'})`
                                : agent.leadGoal
                                ? `Current goal: ${agent.leadGoal} leads`
                                : 'No goal set'}
                            </p>
                          </div>
                          <Input
                            type="number"
                            // Names the metric + period from the selects above, so a
                            // monthly figure isn't typed into a row that saves "daily".
                            placeholder={`${GOAL_METRICS[goalType]?.short || "Goal"} · ${goalDuration}`}
                            title={`Saves as ${GOAL_METRICS[goalType]?.label || goalType} (${goalDuration})`}
                            value={individualGoals[agent.agentId] || ""}
                            onChange={(e) => setIndividualGoals(prev => ({
                              ...prev,
                              [agent.agentId]: e.target.value
                            }))}
                            className="w-32"
                          />
                          <Button
                            size="sm"
                            onClick={() => handleApplyIndividualGoal(agent.agentId)}
                            disabled={isSaving || !individualGoals[agent.agentId]}
                          >
                            Set
                          </Button>
                          {(agent.goalValue || agent.leadGoal) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleClearGoal(agent.agentId)}
                              disabled={isSaving}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                    )}
                  </div>
                </div>
              </ResponsiveDialogContent>
            </Dialog>
          )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto">
        <div className="space-y-4">
          {sortedAgents.length === 0 && searchQuery.trim() !== "" && (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
              <Users className="h-10 w-10 mb-2 text-gray-300" />
              <p className="text-sm">No agents match &quot;{searchQuery}&quot;</p>
            </div>
          )}
          {sortedAgents.map((agent) => {
            // Use leadGoal if set, otherwise show no progress bar (0%)
            // leadGoal is the custom target (e.g., 100) that the agent should complete
            // completedLeads counts only "Sold & Approved" status for progress
            // maxLeads is still used for validation (ensuring they don't exceed their tier limit)
            // Configurable goal: prefer the activity goal (goalType/goalValue);
            // fall back to the legacy lead_count goal (completedLeads / leadGoal).
            const gType = agent.goalType || (agent.leadGoal && agent.leadGoal > 0 ? 'lead_count' : null);
            const gValue = agent.goalValue ?? (agent.leadGoal && agent.leadGoal > 0 ? agent.leadGoal : null);
            const gCurrent = agent.goalType ? (agent.goalCurrent ?? 0) : (agent.completedLeads || 0);
            const gMetric = GOAL_METRICS[gType || 'lead_count'];
            // Thousands separators + the metric's prefix. A premium quota reads
            // "$8,450/$12,000"; every existing metric has no prefix and small numbers,
            // so this renders them exactly as before.
            const fmtGoal = (n: number) =>
              `${gMetric.prefix || ''}${Number(n || 0).toLocaleString('en-US')}`;
            const hasGoal = !!gValue && gValue > 0;
            const completed = gCurrent;
            const workloadPercentage = hasGoal ? (completed / (gValue || 1)) * 100 : 0;
            const isOverloaded = hasGoal && workloadPercentage >= 90;
            const isExpanded = expandedAgentId === agent.agentId;
            const agentLeads = agentLeadsMap[agent.agentId] || [];

            // Status breakdown for mini stats
            const statusBreakdown = agentLeads.reduce<Record<string, number>>((acc, lead) => {
              const stat = lead.leadStat || 'New lead';
              acc[stat] = (acc[stat] || 0) + 1;
              return acc;
            }, {});
            const statusEntries = Object.entries(statusBreakdown).sort((a, b) => b[1] - a[1]);

            // Activity stats
            const neverCalled = agentLeads.filter(l => !l.lastCall).length;
            const stale = agentLeads.filter(l => {
              const days = getDaysAgo(l.lastCall || null);
              return days !== null && days > 14;
            }).length;

            return (
              <div
                key={agent.agentId}
                className={`border rounded-lg transition-all ${isExpanded ? 'border-primary/30 shadow-sm bg-white' : 'border-gray-200 hover:bg-gray-50'}`}
              >
                {/* Clickable Header */}
                <div
                  className="p-4 cursor-pointer select-none"
                  onClick={() => { setExpandedAgentId(isExpanded ? null : agent.agentId); setAgentLeadsPage(1); setAgentLeadsTotal(null); }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium text-gray-900">
                          {agent.agentName}
                          {agent.agencyName && (
                            <span className="text-primary font-normal"> - {agent.agencyName}</span>
                          )}
                        </h3>
                        <Badge className={`text-xs ${getStatusColor(agent.status)}`}>
                          {formatStatus(agent.status)}
                        </Badge>
                        {isOverloaded && (
                          <AlertCircle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <p className="text-sm text-gray-600">{agent.email}</p>
                        {isExpanded
                          ? <ChevronUp className="h-3.5 w-3.5 text-gray-400" />
                          : <ChevronDown className="h-3.5 w-3.5 text-gray-400" />
                        }
                      </div>
                    </div>

                    <div className="text-right">
                      <div className={`text-lg font-semibold ${getWorkloadColor(completed, gValue ?? undefined)}`}>
                        {fmtGoal(completed)}{hasGoal ? `/${fmtGoal(gValue as number)}` : ''}{gMetric.unit && hasGoal ? gMetric.unit : ''}
                      </div>
                      <div className="text-xs text-gray-500">
                        {hasGoal ? `${gMetric.short} · ${agent.goalDuration || 'daily'}` : 'completed'}
                      </div>
                      <div className="flex items-center justify-end gap-1.5 mt-1">
                        <div
                          className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-red-50 border border-red-100"
                          title={`${agent.activeLeadsCount.toLocaleString()} assigned`}
                        >
                          <span className="text-xs font-semibold text-red-600 tabular-nums">{formatCompact(agent.activeLeadsCount)}</span>
                          <span className="text-[11px] text-red-500/90">assigned</span>
                        </div>
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-blue-50 border border-blue-100 cursor-help">
                                <span className="text-xs font-semibold text-blue-600 tabular-nums">{formatCompact(agent.ownedLeads ?? 0)}</span>
                                <span className="text-[11px] text-blue-500/90">uploaded</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs text-xs">
                              <span className="font-semibold">{(agent.ownedLeads ?? 0).toLocaleString()} personal leads</span> this agent uploaded themselves — not assigned to them by anyone. Counted separately from &quot;assigned.&quot;
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        {/* Worked on = leads actually worked by ANY channel. The
                            only one of these three that reflects a setter's
                            output: admins working an owner's shared book stay
                            near 0 "assigned" forever. */}
                        <TooltipProvider delayDuration={150}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-emerald-50 border border-emerald-100 cursor-help">
                                <span className="text-xs font-semibold text-emerald-600 tabular-nums">{formatCompact(agent.workedLeads ?? 0)}</span>
                                <span className="text-[11px] text-emerald-500/90">worked on</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" className="max-w-xs text-xs">
                              <span className="font-semibold">{(agent.workedLeads ?? 0).toLocaleString()} leads worked on</span> by this agent — called, noted, texted, booked or door-knocked — whoever they were assigned to.
                              {(agent.sharedBookLeads ?? 0) > 0 && (
                                <>
                                  {" "}They share a book of{" "}
                                  <span className="font-semibold">{(agent.sharedBookLeads ?? 0).toLocaleString()}</span>{" "}
                                  with the other owners/admins in their agency — that book is one pool, not one per person.
                                </>
                              )}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                      {agent.activeLeadsCount > 0 && (agent.assignedBreakdown?.length ?? 0) > 0 && (
                        <ul
                          className={`mt-1.5 space-y-0.5 text-right ${
                            agent.assignedBreakdown!.length > 3
                              ? 'max-h-[4.25rem] overflow-y-auto pr-0.5 pb-0.5 [scrollbar-width:thin]'
                              : ''
                          }`}
                        >
                          {agent.assignedBreakdown!.map((row, i) => (
                            <li
                              key={`${row.sourceAgencyId ?? 'na'}-${row.assignedByUserId ?? 'na'}-${i}`}
                              className="text-[10.5px] text-gray-500 leading-tight"
                            >
                              <span className="tabular-nums font-medium text-gray-700">{row.count}</span>
                              {' from '}
                              <span className="font-medium text-gray-700">{row.sourceAgencyName}</span>
                              {row.assignedByName && row.assignedByName !== 'System / Legacy' && (
                                <span className="text-gray-400"> · by {row.assignedByName}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">
                        {hasGoal ? `${gMetric.short} goal (${agent.goalDuration || 'daily'})` : 'Progress'}
                      </span>
                      <span className="text-gray-900">
                        {hasGoal ? `${completed} / ${gValue} · ${workloadPercentage.toFixed(0)}%` : "No goal set"}
                      </span>
                    </div>
                    <Progress value={hasGoal ? Math.min(workloadPercentage, 100) : 0} className="h-2" />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-green-600" />
                      <span className="text-gray-600">Conversion:</span>
                      <span className="font-medium">{agent.conversionRate}%</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4 text-blue-600" />
                      <span className="text-gray-600">Total Call Time:</span>
                      <span className="font-medium">{agent.avgResponseTime}</span>
                    </div>
                  </div>

                  <div className="mt-2 text-xs text-gray-500 flex items-center gap-1">
                    Last activity: {formatDate(agent.lastActivity)}
                    <TooltipProvider delayDuration={200}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="w-3 h-3 text-gray-400 hover:text-gray-600 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="max-w-xs text-xs">
                          Updates when the agent makes calls, sends SMS/emails, adds notes, or changes lead statuses.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>
                </div>

                {/* Expanded Section */}
                {isExpanded && (
                  <div className="border-t border-gray-100 px-4 pb-4 animate-in slide-in-from-top-1 duration-200">
                    {/* Status Breakdown Mini-Bar */}
                    {agentLeads.length > 0 && (
                      <div className="mt-3 mb-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Lead Status Breakdown</span>
                          <span className="text-xs text-gray-400">({agentLeads.length} total)</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {statusEntries.map(([status, count]) => (
                            <span
                              key={status}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                              style={getLeadStatusInline(status)}
                            >
                              {getStatusDisplayName(status)} <span className="font-bold">{count}</span>
                            </span>
                          ))}
                        </div>
                        {/* Activity warnings */}
                        {(neverCalled > 0 || stale > 0) && (
                          <div className="flex gap-3 mt-2">
                            {neverCalled > 0 && (
                              <span className="inline-flex items-center gap-1 text-xs text-orange-600">
                                <PhoneOff className="h-3 w-3" />
                                {neverCalled} never called
                              </span>
                            )}
                            {stale > 0 && (
                              <span className="inline-flex items-center gap-1 text-xs text-red-600">
                                <Clock className="h-3 w-3" />
                                {stale} stale (14d+)
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Pager — leads load 50 at a time so big books don't choke the page */}
                    {(agentLeadsPage > 1 || hasNextLeadsPage || (agentLeadsTotalPages != null && agentLeadsTotalPages > 1)) && (
                      <div className="flex items-center justify-between mb-2 text-xs text-gray-500">
                        <span>
                          {agentLeadsTotal != null
                            ? `${agentLeadsTotal.toLocaleString()} lead${agentLeadsTotal !== 1 ? 's' : ''} · page ${agentLeadsPage}${agentLeadsTotalPages ? ` of ${agentLeadsTotalPages}` : ''}`
                            : `Page ${agentLeadsPage} · ${agentLeads.length} shown`}
                          {agentLeadsFetching ? ' …' : ''}
                        </span>
                        <div className="flex items-center gap-1">
                          <Button variant="outline" size="sm" className="h-6 px-2 text-xs"
                            disabled={agentLeadsPage === 1 || agentLeadsFetching}
                            onClick={() => setAgentLeadsPage((p) => Math.max(1, p - 1))}>
                            <ChevronLeft className="h-3 w-3" /> Prev
                          </Button>
                          <Button variant="outline" size="sm" className="h-6 px-2 text-xs"
                            disabled={agentLeadsFetching || (agentLeadsTotalPages != null ? agentLeadsPage >= agentLeadsTotalPages : !hasNextLeadsPage)}
                            onClick={() => setAgentLeadsPage((p) => p + 1)}>
                            Next <ChevronRight className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Assigned Leads List */}
                    {isLoadingExpandedAgent ? (
                      <div className="py-4 flex items-center justify-center gap-2 text-sm text-gray-400">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Loading assigned leads…</span>
                      </div>
                    ) : agentLeads.length === 0 ? (
                      <div className="py-4 text-center text-sm text-gray-400">
                        No leads currently assigned to this agent
                      </div>
                    ) : (
                      <div className="space-y-1 max-h-[320px] overflow-y-auto pr-0.5 [scrollbar-width:thin]">
                        {agentLeads.map((lead) => {
                          const daysAgo = getDaysAgo(lead.lastCall || null);
                          const isStale = daysAgo !== null && daysAgo > 14;
                          const neverContacted = !lead.lastCall;
                          const name = (lead.name || 'Unnamed').trim();
                          const initials = name
                            .split(/\s+/)
                            .slice(0, 2)
                            .map((w: string) => w[0]?.toUpperCase() ?? '')
                            .join('') || 'U';
                          const relativeLastCall =
                            daysAgo === null
                              ? null
                              : daysAgo === 0
                              ? 'last call today'
                              : daysAgo === 1
                              ? 'last call yesterday'
                              : daysAgo < 30
                              ? `last call ${daysAgo} day${daysAgo === 1 ? '' : 's'} ago`
                              : daysAgo < 365
                              ? `last call ${Math.round(daysAgo / 30)} month${Math.round(daysAgo / 30) === 1 ? '' : 's'} ago`
                              : `last call ${Math.round(daysAgo / 365)} year${Math.round(daysAgo / 365) === 1 ? '' : 's'} ago`;

                          const callSeconds = (lead as any).callDurationSeconds || 0;
                          const callCount = (lead as any).callCount || 0;
                          const totalCallTime =
                            callSeconds === 0
                              ? null
                              : callSeconds < 60
                              ? `${callSeconds}s total`
                              : callSeconds < 3600
                              ? `${Math.round(callSeconds / 60)}m total`
                              : `${Math.floor(callSeconds / 3600)}h ${Math.round((callSeconds % 3600) / 60)}m total`;

                          return (
                            <div
                              key={lead.id}
                              className="group relative flex items-center gap-2.5 py-2 pl-2 pr-1.5 rounded-lg bg-white border border-gray-100 hover:border-gray-200 hover:shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition-all"
                            >
                              {/* Avatar */}
                              <div
                                className="h-8 w-8 flex-shrink-0 rounded-full flex items-center justify-center text-[10.5px] font-semibold text-white tracking-wide bg-gradient-to-br from-slate-500 to-slate-700 ring-1 ring-inset ring-white/20 shadow-[0_1px_0_rgba(0,0,0,0.04)]"
                                aria-hidden="true"
                              >
                                {initials}
                              </div>

                              {/* Body */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <span className="font-semibold text-[13px] text-gray-900 truncate leading-tight">
                                    {name}
                                  </span>
                                  <span
                                    className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 h-[18px] rounded-full text-[9.5px] font-semibold uppercase tracking-wide leading-none"
                                    style={getLeadStatusInline(lead.leadStat)}
                                  >
                                    <span className="h-1 w-1 rounded-full bg-current opacity-70" />
                                    {getStatusDisplayName(lead.leadStat || 'New lead')}
                                  </span>
                                  {neverContacted && (
                                    <PhoneOff
                                      className="h-3 w-3 text-amber-500 flex-shrink-0"
                                      aria-label="Never called"
                                    />
                                  )}
                                  {isStale && !neverContacted && (
                                    <Clock
                                      className="h-3 w-3 text-red-400 flex-shrink-0"
                                      aria-label={`Stale — last called ${daysAgo}d ago`}
                                    />
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-gray-500 min-w-0">
                                  {lead.phone && (
                                    <span className="inline-flex items-center gap-1 tabular-nums truncate">
                                      <Phone className="h-2.5 w-2.5 text-gray-400 flex-shrink-0" />
                                      {formatPhone(lead.phone)}
                                    </span>
                                  )}
                                  {lead.state && (
                                    <>
                                      <span className="text-gray-300" aria-hidden="true">·</span>
                                      <span className="font-medium text-gray-600">{lead.state}</span>
                                    </>
                                  )}
                                  {relativeLastCall ? (
                                    <>
                                      <span className="text-gray-300" aria-hidden="true">·</span>
                                      <span
                                        className={isStale ? 'text-red-500' : 'text-gray-500'}
                                        title={formatDate(lead.lastCall ?? null)}
                                      >
                                        {relativeLastCall}
                                      </span>
                                    </>
                                  ) : (
                                    <>
                                      <span className="text-gray-300" aria-hidden="true">·</span>
                                      <span className="text-amber-500">never called</span>
                                    </>
                                  )}
                                  {totalCallTime && (
                                    <>
                                      <span className="text-gray-300" aria-hidden="true">·</span>
                                      <span
                                        className="inline-flex items-center gap-0.5 text-blue-600 font-medium tabular-nums"
                                        title={`${callCount} call${callCount === 1 ? '' : 's'}`}
                                      >
                                        <Clock className="h-2.5 w-2.5" />
                                        {totalCallTime}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Quick Actions — translate-in on hover for a subtler reveal */}
                              <div className="flex items-center gap-0.5 opacity-0 translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-150 flex-shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 rounded-md hover:bg-gray-100"
                                  title="View lead details"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    // Shell-aware: native lead screen on /m
                                    router.push(
                                      window.location.pathname.startsWith("/m")
                                        ? `/m/leads/${lead.id}`
                                        : `/leads/${lead.id}`,
                                    );
                                  }}
                                >
                                  <Eye className="h-3.5 w-3.5 text-gray-500" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 w-7 p-0 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50"
                                  title="Unassign lead"
                                  disabled={unassignMutation.isPending}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleUnassign(lead.id, agent.agentId);
                                  }}
                                >
                                  <UserMinus className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
